import React, { useState } from 'react';
import { Check, CheckCircle2, Clock, AlertTriangle, XCircle, Copy, HelpCircle, Mail, Camera, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../../../components/ui/input';
import { Dropdown } from '../../../components/ui/Dropdown';
import { Modal } from '../../../components/ui/Modal';
import { getPermissionsByRole, togglePermissionWithCoherence } from '../../../auth/permissions';
import { api } from '../../../api/client';
import { formatCargo } from '../Equipe';

const ROLES = ['DIRETOR', 'VICE_DIRETOR', 'COORDENACAO', 'SECRETARIA'];
const CARGOS = ['Diretor(a)', 'Vice Diretor', 'Coordenador(a)', 'Supervisor(a)', 'Orientador(a)', 'Secretário(a)'];

const PERFIL_OPTIONS = ROLES.map(r => ({ value: r, label: r === 'VICE_DIRETOR' ? 'VICE DIRETOR' : r.toUpperCase() }));
const CARGO_OPTIONS = CARGOS.map(c => ({ value: c, label: c }));

const PERMISSION_STRUCTURE = [
  {
    title: 'Equipe e Configuração',
    resources: [
      { label: 'Equipe', viewKey: 'equipe.view', manageKey: 'equipe.manage' },
      { label: 'Gestão Escolar', viewKey: 'gestao.view', manageKey: 'gestao.manage' },
      { label: 'Escola (admin)', manageKey: 'escola.manage' },
      { label: 'Configurações', manageKey: 'configuracoes.manage' },
      { label: 'Dashboard', viewKey: 'dashboard.view' },
    ],
  },
  {
    title: 'Acadêmico',
    resources: [
      { label: 'Turnos', viewKey: 'turnos.view', manageKey: 'turnos.manage' },
      { label: 'Matérias', viewKey: 'materias.view', manageKey: 'materias.manage' },
      { label: 'Turmas', viewKey: 'turmas.view', manageKey: 'turmas.manage' },
      { label: 'Horários', viewKey: 'horarios.view', manageKey: 'horarios.manage' },
      { label: 'Relatórios', viewKey: 'relatorios.view' },
    ],
  },
  {
    title: 'Pedagógico',
    resources: [
      { label: 'Avaliações e Notas', viewKey: 'avaliacoes.view', manageKey: 'avaliacoes.manage' },
      { label: 'Observações do Aluno', viewKey: 'observacoes.view', manageKey: 'observacoes.manage' },
    ],
  },
  {
    title: 'Pessoas e Contas',
    resources: [
      { label: 'Professores', viewKey: 'professores.view', manageKey: 'professores.manage' },
      { label: 'Alunos', viewKey: 'alunos.view', manageKey: 'alunos.manage' },
      { label: 'Responsáveis', viewKey: 'responsaveis.view', manageKey: 'responsaveis.manage' },
    ],
  },
  {
    title: 'Operacional (Frequência, Recados, RFID)',
    resources: [
      { label: 'Frequência / Chamadas', viewKey: 'chamadas.view', manageKey: 'chamadas.manage' },
      { label: 'Comunicados / Recados', viewKey: 'comunicados.view', manageKey: 'comunicados.manage' },
      { label: 'Mídias / Galeria', viewKey: 'midias.view', manageKey: 'midias.manage' },
      { label: 'Acesso / Portaria', viewKey: 'acesso.view', manageKey: 'acesso.manage' },
      { label: 'Dispositivos / Catracas', viewKey: 'dispositivos.view', manageKey: 'dispositivos.manage' },
      { label: 'Incidentes de Segurança', viewKey: 'security.incidentes-escola.view' },
    ],
  },
];

interface InviteMemberModalProps {
  open: boolean;
  onClose: () => void;
  editing: any;
  saving: boolean;
  onSave: () => Promise<void>;
  gerarNovoLink: (u: any) => Promise<void>;
  gerando: string | null;
  userId: string | null | undefined;
  canManagePermissions: boolean;
  form: any;
  setForm: React.Dispatch<React.SetStateAction<any>>;
  fieldErrors: Record<string, string>;
  setFieldErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  avatarInitial: string;
  avatarFileInputRef: React.RefObject<HTMLInputElement | null>;
  onAvatarFilePicked: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fotoUploading: boolean;
  formatPhone: (v: string) => string;
  equipe: any[];
  equipeModalTab: 'dados' | 'permissoes';
  setEquipeModalTab: React.Dispatch<React.SetStateAction<'dados' | 'permissoes'>>;
}

export function InviteMemberModal({
  open,
  onClose,
  editing,
  saving,
  onSave,
  gerarNovoLink,
  gerando,
  userId,
  canManagePermissions,
  form,
  setForm,
  fieldErrors,
  setFieldErrors,
  avatarInitial,
  avatarFileInputRef,
  onAvatarFilePicked,
  fotoUploading,
  formatPhone,
  equipe,
  equipeModalTab,
  setEquipeModalTab,
}: InviteMemberModalProps) {
  const [showInfoPopover, setShowInfoPopover] = useState(false);
  const infoRef = React.useRef<HTMLDivElement>(null);
  const modalBodyRef = React.useRef<HTMLDivElement>(null);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
    [PERMISSION_STRUCTURE[0].title]: true,
  });

  const toggleGroup = (title: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const scrollToTop = () => {
    setTimeout(() => {
      modalBodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }, 50);
  };

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (showInfoPopover && infoRef.current && !infoRef.current.contains(event.target as Node)) {
        setShowInfoPopover(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showInfoPopover]);

  // Mapeamento automático de Cargo para o Perfil de acesso mais próximo
  const getClosestPerfil = (cargo: string): string => {
    if (!cargo) return 'COORDENACAO';
    const c = cargo.toLowerCase();
    if (c.includes('vice')) return 'VICE_DIRETOR';
    if (c.includes('diretor')) return 'DIRETOR';
    if (c.includes('secret') || c.includes('auxiliar')) return 'SECRETARIA';
    if (c.includes('coordenador') || c.includes('supervisor') || c.includes('orientador')) return 'COORDENACAO';
    return 'COORDENACAO'; // Fallback padrão
  };

  const handlePerfilChange = (newPerfil: string) => {
    setForm((prev: any) => {
      return {
        ...prev,
        perfil: newPerfil,
        permissaoPreset: 'PADRAO', // Sempre redefine para padrão ao mudar o perfil explicitamente
        permissoes: getPermissionsByRole(newPerfil, prev.cargo), // Sempre carrega as permissões do novo perfil
      };
    });
  };

  const handleCargoChange = (newCargo: string) => {
    setForm((prev: any) => {
      const closestPerfil = getClosestPerfil(newCargo);
      const updated = {
        ...prev,
        cargo: newCargo,
        perfil: closestPerfil,
      };
      if (prev.permissaoPreset === 'PADRAO') {
        updated.permissoes = getPermissionsByRole(closestPerfil, newCargo);
      }
      return updated;
    });
  };

  const togglePermission = (key: string) => {
    if (!canManagePermissions || editing?.id === userId) {
      toast.error(
        editing?.id === userId
          ? 'Você não pode alterar suas próprias permissões de acesso.'
          : 'Somente Diretor(a) ou Administrador pode alterar permissões da equipe.',
      );
      return;
    }
    setForm((f: any) => {
      const nextPermissoes = togglePermissionWithCoherence(f.permissoes, key);
      return {
        ...f,
        permissaoPreset: 'PERSONALIZADO', // Muda automaticamente para personalizado ao interagir com as checkboxes
        permissoes: nextPermissoes,
      };
    });
  };

  const setField = (key: string, value: any) => {
    setForm((prev: any) => ({ ...prev, [key]: value }));
    if (fieldErrors[key]) {
      setFieldErrors((prev: any) => {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      });
    }
  };

  const [validatingEmail, setValidatingEmail] = useState(false);

  const validateAndProceed = async () => {
    const errors: Record<string, string> = {};

    // 1. Validar Nome
    if (!form.nome.trim()) {
      errors.nome = 'Nome é obrigatório';
    } else if (form.nome.trim().length < 3) {
      errors.nome = 'Nome deve ter ao menos 3 caracteres';
    }

    // 2. Validar E-mail localmente
    if (!form.email.trim()) {
      errors.email = 'E-mail é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errors.email = 'E-mail inválido';
    } else {
      // Duplicidade local (mesma escola)
      const emailDup = equipe.find(
        (u: any) =>
          u.email?.toLowerCase() === form.email.trim().toLowerCase() &&
          u.id !== editing?.id
      );
      if (emailDup) {
        errors.email = `E-mail já cadastrado nesta escola (${emailDup.nome})`;
      }
    }

    // 3. Validar WhatsApp localmente
    const phoneDigits = form.telefone.replace(/\D/g, '');
    if (!phoneDigits || phoneDigits.length < 10) {
      errors.telefone = 'WhatsApp deve ter ao menos 10 dígitos';
    } else {
      // Duplicidade local (mesma escola)
      const telDuplicado = equipe.find(
        (u: any) =>
          u.id !== editing?.id &&
          (u.telefone || '').replace(/\D/g, '') === phoneDigits &&
          phoneDigits.length >= 10
      );
      if (telDuplicado) {
        errors.telefone = `WhatsApp já cadastrado para "${telDuplicado.nome}"`;
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    // 4. Validar E-mail globalmente (se for novo ou tiver mudado)
    const emailChanged = !editing || form.email.trim().toLowerCase() !== editing.email.trim().toLowerCase();
    if (emailChanged) {
      setValidatingEmail(true);
      try {
        const { data } = await api.post('/auth/check-email', {
          email: form.email.trim().toLowerCase()
        });
        if (data.exists) {
          setFieldErrors((prev: any) => ({
            ...prev,
            email: 'Este e-mail já está cadastrado em outra escola ou rede na plataforma Aluno Conectado.'
          }));
          setValidatingEmail(false);
          return;
        }
      } catch (err) {
        // Prossegue se houver falha de rede/verificação, para não bloquear o usuário caso o endpoint esteja indisponível.
      }
      setValidatingEmail(false);
    }

    setFieldErrors({});
    setEquipeModalTab('permissoes');
    scrollToTop();
  };

  return (
    <Modal open={open} onClose={onClose} title="" size="lg" contentClassName="umu-wizard-modal" noBodyScroll dialogId="modal-convidar-membro">
      {/* Banner */}
      <div className="umu-wizard-banner" style={{
        background: 'linear-gradient(135deg, #1e3a5f 0%, #1e40af 40%, #3730a3 100%)',
        borderRadius: '12px 12px 0 0', padding: '14px 20px 12px', position: 'relative', overflow: 'hidden',
        flexShrink: 0,
        flex: '0 0 auto',
      }}>
        <div style={{ position: 'absolute', top: -40, right: -40, width: 140, height: 140, borderRadius: '50%', background: 'rgba(99,102,241,.12)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h2 style={{ color: 'white', fontSize: '1.05rem', fontWeight: 800, margin: 0 }}>
            {editing ? 'Editar Membro' : 'Convidar Membro'}
          </h2>
        </div>

        {/* Stepper */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10 }}>
          {[
            { n: 1, label: 'Identificação', tab: 'dados' },
            { n: 2, label: 'Perfil / Permissões', tab: 'permissoes' }
          ].map((s, i, arr) => {
            const isCurrent = equipeModalTab === s.tab;
            const isDone = (equipeModalTab === 'permissoes' && s.tab === 'dados');
            return (
              <div key={s.n} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button
                  type="button"
                  onClick={() => {
                    if (s.tab === 'dados') {
                      setEquipeModalTab('dados');
                      scrollToTop();
                    } else {
                      void validateAndProceed();
                    }
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  <div style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '.68rem',
                    fontWeight: 700,
                    background: isCurrent ? 'white' : isDone ? '#10b981' : 'rgba(255,255,255,.15)',
                    color: isCurrent ? '#1e40af' : isDone ? 'white' : 'rgba(255,255,255,.5)',
                  }}>
                    {isDone ? <CheckCircle2 size={12} /> : s.n}
                  </div>
                  <span style={{ color: isCurrent ? 'white' : 'rgba(255,255,255,.6)', fontSize: '.72rem', fontWeight: 600 }}>{s.label}</span>
                </button>
                {i < arr.length - 1 && <div style={{ width: 24, height: 2, background: isDone ? '#10b981' : 'rgba(255,255,255,.15)', borderRadius: 2 }} />}
              </div>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div ref={modalBodyRef} className="umu-wizard-body p-4 sm:p-5 flex-1 overflow-y-auto min-h-0">
        {editing && (() => {
          const status = (editing as any).statusConta;
          const isPendente = status === 'PENDENTE' || status === 'FALHOU';
          const criadoEm = (editing as any).criadoEm ? new Date((editing as any).criadoEm) : null;
          const fmt = (d: Date) => d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
          const expiraEm = criadoEm ? new Date(criadoEm.getTime() + 72 * 60 * 60 * 1000) : null;
          const expirado = expiraEm ? new Date() > expiraEm : false;

          return (
            <div style={{
              padding: '10px 14px', borderRadius: 10, marginBottom: 16,
              background: isPendente ? (status === 'FALHOU' ? 'rgba(239,68,68,0.06)' : expirado ? 'rgba(239,68,68,0.06)' : 'rgba(245,158,11,0.06)') : 'rgba(16,185,129,0.06)',
              border: `1px solid ${isPendente ? (status === 'FALHOU' ? 'rgba(239,68,68,0.2)' : expirado ? 'rgba(239,68,68,0.2)' : 'rgba(245,158,11,0.2)') : 'rgba(16,185,129,0.2)'}`,
            }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  {isPendente
                    ? (status === 'FALHOU' ? <XCircle size={16} style={{ color: '#ef4444', flexShrink: 0 }} /> : expirado ? <AlertTriangle size={16} style={{ color: '#ef4444', flexShrink: 0 }} /> : <Clock size={16} style={{ color: '#f59e0b', flexShrink: 0 }} />)
                    : <CheckCircle2 size={16} style={{ color: '#10b981', flexShrink: 0 }} />
                  }
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '.8rem', fontWeight: 700, lineHeight: 1.2, color: isPendente ? (status === 'FALHOU' ? '#dc2626' : expirado ? '#991b1b' : '#92400e') : '#065f46' }}>
                      {status === 'FALHOU' ? 'Email Falhou' : isPendente ? (expirado ? 'Convite Expirado' : 'Convite Pendente') : 'Conta Ativa'}
                    </div>
                    <div style={{ fontSize: '.72rem', lineHeight: 1.25, color: '#6b7280' }}>
                      {status === 'FALHOU'
                        ? 'O email foi rejeitado. Corrija e reenvie.'
                        : isPendente
                          ? (expirado ? 'O link expirou. Gere um novo link.' : 'Aguardando ativação pelo membro')
                          : 'Membro já ativou sua conta'
                      }
                    </div>
                  </div>
                </div>
                {isPendente && (
                  <button
                    type="button"
                    onClick={() => gerarNovoLink(editing)}
                    disabled={gerando === editing.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px',
                      borderRadius: 8, border: '1px solid rgba(99,102,241,0.25)',
                      background: 'rgba(99,102,241,0.06)', color: '#6366f1',
                      fontSize: '.74rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >
                    <Copy size={12} />
                    {gerando === editing.id ? 'Gerando...' : expirado ? 'Gerar Novo Link' : 'Gerar Link e Copiar'}
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        {equipeModalTab === 'dados' && (
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-center sm:items-start">
            {/* Foto Upload */}
            <div className="flex flex-col items-center gap-1.5 shrink-0">
              <button
                type="button"
                className={`ac-equipe-avatar ${!editing ? 'ac-equipe-avatar--novo' : ''} ${fotoUploading ? 'ac-equipe-avatar--busy' : ''}`}
                onClick={() => {
                  if (fotoUploading) return;
                  avatarFileInputRef.current?.click();
                }}
                style={{
                  width: 84,
                  height: 84,
                  borderRadius: '50%',
                  border: '2px dashed #cbd5e1',
                  background: '#f8fafc',
                  position: 'relative',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title={fotoUploading ? 'Enviando foto...' : (form.fotoUrl ? 'Clique para alterar a foto' : 'Clique para adicionar foto')}
              >
                {form.fotoUrl ? (
                  <img src={form.fotoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span style={{ fontSize: '1.6rem', fontWeight: 700, color: '#64748b' }}>
                    {avatarInitial}
                  </span>
                )}
                {editing && !fotoUploading && (
                  <span className="ac-equipe-avatar__cam" style={{ position: 'absolute', bottom: 4, right: 4 }}>
                    <Camera size={10} />
                  </span>
                )}
              </button>
              <input
                ref={avatarFileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                style={{ display: 'none' }}
                onChange={onAvatarFilePicked}
              />
              <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>Foto (Opcional)</span>
            </div>

            {/* Grid de campos */}
            <div className="w-full flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-3.5">
              <div className="form-group">
                <label className="form-label" htmlFor="member-nome">Nome Completo *</label>
                <Input
                  id="member-nome"
                  name="nome"
                  data-lab-field="nome"
                  className={fieldErrors.nome ? 'border-red-500 ring-red-500/20' : ''}
                  value={form.nome}
                  onChange={(e) => setField('nome', e.target.value)}
                  placeholder="Nome do membro"
                />
                {fieldErrors.nome && <span className="form-error">{fieldErrors.nome}</span>}
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="member-email">E-mail *</label>
                <Input
                  id="member-email"
                  name="email"
                  data-lab-field="email"
                  className={fieldErrors.email ? 'border-red-500 ring-red-500/20' : ''}
                  type="email"
                  value={form.email}
                  onChange={(e) => setField('email', e.target.value)}
                  placeholder="email@escola.edu.br"
                  disabled={editing?.id === userId}
                />
                {fieldErrors.email && <span className="form-error">{fieldErrors.email}</span>}
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="member-telefone">WhatsApp *</label>
                <Input
                  id="member-telefone"
                  name="telefone"
                  data-lab-field="telefone"
                  className={fieldErrors.telefone ? 'border-red-500 ring-red-500/20' : ''}
                  type="tel"
                  value={form.telefone}
                  onChange={(e) => setField('telefone', formatPhone(e.target.value))}
                  placeholder="(31) 99999-9999"
                  maxLength={15}
                />
                {fieldErrors.telefone && <span className="form-error">{fieldErrors.telefone}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Cargo</label>
                <div data-lab-field="cargo">
                  <Dropdown
                    options={CARGO_OPTIONS}
                    value={formatCargo(form.cargo) !== '—' ? formatCargo(form.cargo) : form.cargo}
                    onChange={(val) => handleCargoChange(val as string)}
                    disabled={editing?.id === userId}
                    placeholder="Selecione..."
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {equipeModalTab === 'dados' && !editing && (
          <div className="hidden md:flex items-start gap-3 p-3.5 rounded-xl bg-indigo-50/50 border border-indigo-100 mt-4">
            <Mail size={16} className="text-indigo-600 shrink-0 mt-0.5" />
            <div className="flex flex-col gap-0.5">
              <div className="font-bold text-xs text-indigo-950">Link de ativação seguro</div>
              <div className="text-xs text-indigo-700 leading-snug">
                O membro definirá a própria senha ao acessar o <strong>link de ativação seguro</strong>. Ao salvar, você poderá copiar o link para enviar a ele.
              </div>
            </div>
          </div>
        )}

        {equipeModalTab === 'permissoes' && (
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-1">
              {/* Perfil de Acesso */}
              <div className="form-group">
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <label className="form-label" style={{ marginBottom: 0 }}>Perfil de Acesso *</label>
                  <div ref={infoRef} style={{ position: 'relative' }}>
                    <button
                      type="button"
                      onClick={() => setShowInfoPopover(!showInfoPopover)}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                        color: '#9ca3af', display: 'flex', alignItems: 'center',
                        transition: 'color 0.15s',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = '#6366f1')}
                      onMouseLeave={(e) => (e.currentTarget.style.color = '#9ca3af')}
                      title="O que é Perfil de Acesso?"
                    >
                      <HelpCircle size={14} />
                    </button>
                    {showInfoPopover && (
                      <div style={{
                        position: 'absolute', top: 'calc(100% + 8px)', left: 0,
                        width: 300, padding: 14, borderRadius: 12,
                        background: 'var(--bg-card, white)', color: 'var(--text-main, #1f2937)',
                        border: '1px solid var(--border-color, #e5e7eb)',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.15)', zIndex: 9999,
                        fontSize: '.8rem', lineHeight: 1.5,
                        animation: 'fadeIn 0.15s ease-out',
                      }}>
                        <h4 style={{ fontSize: '.84rem', fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <HelpCircle size={15} style={{ color: '#6366f1' }} />
                          Perfil vs. Cargo
                        </h4>
                        <p style={{ marginBottom: 6 }}>
                          <strong style={{ color: '#6366f1' }}>Perfil</strong> define as <strong>permissões de acesso</strong> no sistema (telas e dados que pode visualizar/gerenciar).
                        </p>
                        <p style={{ marginBottom: 6 }}>
                          <strong style={{ color: '#6366f1' }}>Cargo</strong> é o título funcional na escola (ex: Coordenador, Secretário).
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <Dropdown
                  options={PERFIL_OPTIONS}
                  value={form.perfil}
                  onChange={(val) => handlePerfilChange(val as string)}
                  disabled={editing?.id === userId}
                  placeholder="Selecione..."
                />
              </div>

              {/* Modo de Permissões (Preset) */}
              <div className="form-group">
                <label className="form-label" style={{ marginBottom: 4 }}>Configuração de Permissões</label>
                <select
                  className="form-select ac-equipe-preset-select"
                  value={form.permissaoPreset}
                  onChange={(e) => {
                    const preset = e.target.value as 'PADRAO' | 'PERSONALIZADO';
                    setForm((f: any) => ({
                      ...f,
                      permissaoPreset: preset,
                      permissoes:
                        preset === 'PADRAO'
                          ? getPermissionsByRole('ESCOLA', f.perfil)
                          : f.permissoes,
                    }));
                  }}
                  disabled={!canManagePermissions || editing?.id === userId}
                >
                  <option value="PADRAO">Seguir permissões padrão do perfil</option>
                  <option value="PERSONALIZADO">Personalizar permissões manualmente</option>
                </select>
              </div>
            </div>

            {/* Lista de Permissões com Expander/Accordion */}
            <div className="flex flex-col gap-2 mt-1">
              {PERMISSION_STRUCTURE.map((group) => {
                const isExpanded = !!expandedGroups[group.title];
                const checkedCount = group.resources.reduce((acc, res) => {
                  let count = 0;
                  if (res.viewKey && form.permissoes[res.viewKey]) count++;
                  if (res.manageKey && form.permissoes[res.manageKey]) count++;
                  return acc + count;
                }, 0);

                return (
                  <div
                    key={group.title}
                    className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-xs transition-all"
                  >
                    {/* Header do Expander */}
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.title)}
                      className={`w-full px-3.5 py-2.5 flex items-center justify-between text-left transition-colors cursor-pointer border-none ${isExpanded ? 'bg-slate-50 border-b border-slate-200' : 'bg-white hover:bg-slate-50/70'
                        }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-slate-800 tracking-wide">
                          {group.title}
                        </span>
                        {checkedCount > 0 && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100">
                            {checkedCount} {checkedCount === 1 ? 'ativa' : 'ativas'}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 text-slate-400">
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </div>
                    </button>

                    {/* Conteúdo Expandido */}
                    {isExpanded && (
                      <div className="p-3 sm:p-4 bg-white flex flex-col gap-2">
                        {/* Cabeçalho da Tabela */}
                        <div className="grid grid-cols-[1fr_60px_60px] sm:grid-cols-[2fr_1fr_1fr] gap-2 pb-1.5 border-b border-slate-100 mb-1">
                          <span className="text-[11px] font-bold text-slate-400">RECURSO</span>
                          <span className="text-[11px] font-bold text-slate-400 text-center">VER</span>
                          <span className="text-[11px] font-bold text-slate-400 text-center">EDITAR</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          {group.resources.map((res) => (
                            <div
                              key={res.label}
                              data-lab-resource-row={res.label}
                              className="grid grid-cols-[1fr_60px_60px] sm:grid-cols-[2fr_1fr_1fr] gap-2 items-center py-1 hover:bg-slate-50/60 rounded-md px-1 transition-colors"
                            >
                              <span className="text-xs font-medium text-slate-800 truncate" title={res.label}>
                                {res.label}
                              </span>

                              {/* Checkbox Visualizar */}
                              <div className="flex justify-center">
                                {res.viewKey ? (
                                  <div
                                    data-lab-permission-view={res.viewKey}
                                    data-lab-permission-label={res.label}
                                    onClick={() => togglePermission(res.viewKey!)}
                                    className={`w-6 h-6 rounded-md border flex items-center justify-center transition-all ${form.permissoes[res.viewKey]
                                        ? 'bg-indigo-600 border-indigo-600 text-white shadow-xs'
                                        : 'bg-white border-slate-300 hover:border-slate-400'
                                      } ${(!canManagePermissions || editing?.id === userId) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer active:scale-90 hover:scale-105'}`}
                                    title={!canManagePermissions ? 'Apenas Diretor ou Administrador pode alterar permissões' : (form.permissoes[res.viewKey] ? 'Clique para desativar' : 'Clique para ativar')}
                                  >
                                    {!!form.permissoes[res.viewKey] && <Check size={14} strokeWidth={3} />}
                                  </div>
                                ) : (
                                  <span className="text-xs text-slate-300">-</span>
                                )}
                              </div>

                              {/* Checkbox Gerenciar */}
                              <div className="flex justify-center">
                                {res.manageKey ? (
                                  <div
                                    data-lab-permission-manage={res.manageKey}
                                    data-lab-permission-label={res.label}
                                    onClick={() => togglePermission(res.manageKey!)}
                                    className={`w-6 h-6 rounded-md border flex items-center justify-center transition-all ${form.permissoes[res.manageKey]
                                        ? 'bg-indigo-600 border-indigo-600 text-white shadow-xs'
                                        : 'bg-white border-slate-300 hover:border-slate-400'
                                      } ${(!canManagePermissions || editing?.id === userId) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer active:scale-90 hover:scale-105'}`}
                                    title={!canManagePermissions ? 'Apenas Diretor ou Administrador pode alterar permissões' : (form.permissoes[res.manageKey] ? 'Clique para desativar' : 'Clique para ativar')}
                                  >
                                    {!!form.permissoes[res.manageKey] && <Check size={14} strokeWidth={3} />}
                                  </div>
                                ) : (
                                  <span className="text-xs text-slate-300">-</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {!canManagePermissions && (
              <div style={{ marginTop: 4, fontSize: '.72rem', color: '#b45309' }}>
                Somente Diretor(a) pode alterar permissões granulares.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="modal-footer umu-wizard-footer" style={{ flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 10 }}>
          <button
            type="button"
            className="btn-cancel"
            onClick={() => {
              if (equipeModalTab === 'permissoes') {
                setEquipeModalTab('dados');
                scrollToTop();
              } else {
                onClose();
              }
            }}
            style={{
              padding: '10px 20px',
              borderRadius: 10,
              fontSize: '0.85rem',
              fontWeight: 600,
              border: '1px solid #cbd5e1',
              background: 'white',
              color: '#475569',
              cursor: 'pointer',
            }}
          >
            {equipeModalTab === 'permissoes' ? '← Voltar' : 'Cancelar'}
          </button>

          {equipeModalTab === 'dados' ? (
            <button
              type="button"
              className="btn-save"
              onClick={validateAndProceed}
              disabled={validatingEmail}
              style={{
                padding: '10px 24px',
                borderRadius: 10,
                fontSize: '0.85rem',
                fontWeight: 600,
                border: 'none',
                background: validatingEmail ? '#a5b4fc' : '#6366f1',
                color: 'white',
                cursor: validatingEmail ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
              }}
            >
              {validatingEmail ? 'Verificando...' : 'Próximo →'}
            </button>
          ) : (
            <button
              type="button"
              className="btn-save"
              onClick={onSave}
              disabled={saving}
              style={{
                padding: '10px 24px',
                borderRadius: 10,
                fontSize: '0.85rem',
                fontWeight: 600,
                border: 'none',
                background: '#10b981',
                color: 'white',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)',
              }}
            >
              {saving ? 'Salvando...' : editing ? 'Salvar Alterações' : 'Enviar Convite'}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
